/// <reference path="../../lib/tsd.d.ts" />

import {Options, ReceiverInterface} from "./receiver";
import {Collection} from "../lib/event-interface";
import logging from "../utils/log";

export class WebrtcReceiver implements ReceiverInterface
{
	private TAG = "[Sxc.Chat.WowzaReceiver]";

	/** This is always false - set in initialize */
	private standalone: boolean;

	private $container : JQuery;

	/** Name of video element */
	private videoElementIdName : string = 'sxcc-obj-video';
	/** The video element object */
	private remoteVideo : HTMLMediaElement = null;

	/** true after initialized */
	private initialized:boolean = false;
	/** true while connecting */
	private connecting:boolean  = false;
	/** true if connected and playing */
	private playing:boolean     = false;

	private muteForced : boolean = false;
	private volume : number = null;

	public evt : Collection;

	private sdpURL : string = null;
	private streamInfo = {applicationName:"webrtc", streamName:"myStream", sessionId:"[empty]"};
	private userData : any = {}; // {param1: 'value'};
	private repeaterRetryCount : number = 0;

	private wsConnection : WebSocket = null;
	private peerConnection : RTCPeerConnection = null;

	private reconnectTimer : number = null; // if it is null, it is not active now
	private reconnectTimeout = 5000;
	private reconnectCount : number = 0;
	private reconnectMaxChance = 2;

	private videoFrameDetectionTimer : number = null;
	private videoFrameDetectionFrequency : number = 200; // in ms, between video frame checking
	private videoFrameDetectionTimeout : number = 15;	 // report timeout if video frame is not detected during this time
	private videoFrameDetectionCounter : number ;

	private iceCandidateTcpOnly : boolean = false;

	/**
	 * Create new instance
	 * @param params optional parameters
	 */
	constructor (private params : Options)
	{
		this.evt = new Collection("receiver");
	}

	/**
	 * Name of this receiver
	 */
	public getCurrentName(): string
	{
		return "WowzaWebrtcReceiver";
	}

	/**
	 * This is only used for visitx - the returned videoobject controls cam2cam
	 */
	public getVideoObject(): any {
		return null;
	}

	/**
	 * Some kind of css magic to add or remove classes.
	 */
	public setFlag(s: string, enabled : boolean = false): void
	{
		this.$container[enabled ? "addClass" : "removeClass"](s);
	}

	/**
	 * WebRtc is always supported - we are not doing any detection here
	 * Looks like it's not even used
	 */
	public static isSupported(): boolean
	{
		return true;
	}

	/**
	 * Ready after initialization
	 */
	public isReady(): boolean
	{
		return this.initialized;
	}

	/**
	 * Initialize component, creating new video element for playback
	 * @param $container the container to add the video element to
	 * @param standalone handles mouse enter/leave events
	 */
	public initialize($container, standalone = true): void
	{
		logging.log(this.TAG, "initialize", this.initialized);

		if (this.initialized) return;

		this.standalone = standalone;

		this.$container = $container;

		if (standalone)
		{
			this.$container.on("mouseenter", (e) => {
				this.evt.trigger("mouseenter", e);
			});

			this.$container.on("mouseleave", (e) => {
				this.evt.trigger("mouseleave", e);
			});
		}

		this.$container.find('video').remove();

		let videoElement = $('<video>').attr('id', this.videoElementIdName)
			.attr('autoplay', 'autoplay')
			.attr('playsinline', 'playsinline');

		this.$container.append(videoElement);

		this.remoteVideo = <HTMLMediaElement>videoElement.get(0)

		this.initialized = true;

		this.evt.trigger("ready");
	}

	/**
	 * Another unused one, but keep it just in case
	 */
	public isInitialized(): boolean
	{
		return this.initialized;
	}

	/**
	 * Establish persistent connection to server
	 */
	public connect(): void
	{
		// we don't keep a persistent connection to the server for webrtc streams, connect is no-op
	}

	/**
	 * Disconnect persistent connection - also disconnect pending sessions
	 */
	public disconnect(): void
	{
		logging.log(this.TAG, "[disconnect]");
		this.stop();
	}

	/**
	 * Sets volume of video element.
	 * @param volume 0..100
	 */
	public setVolume(volume : number):void
	{
		logging.log(this.TAG, "setVolume", volume);
		this.volume = volume;
		if (!this.initialized) return;

		let videoElement = <HTMLVideoElement>document.getElementById(this.videoElementIdName);
		videoElement.muted = this.muteForced || (this.volume == 0);
		videoElement.volume = this.volume / 100;
	}


	/**
	 * Destroy everything - needs to reinitialize after
	 * I think this is not used either
	 */
	public destroy():void
	{
		if (!this.initialized) return;
		logging.log(this.TAG, "destroy");
		this.initialized = false;
		this.stop();
		if (this.standalone)
		{
			this.$container.off("mouseenter");
			this.$container.off("mouseleave");
		}

		this.$container.find('video').remove();
	}

	/**
	 * Looks like this on is not used.
	 * @param forced
	 */
	public forceMute(forced: boolean): void
	{
		logging.log(this.TAG, "forceMute", forced);
		this.muteForced = forced;
		this.setVolume(this.volume);
	}

	/**
	 * Initialize UI
	 */
	public clear(): void
	{
		this.$container.css("opacity", 0);
	}

	/**
	 * This is called when stream is broken, we are in free stream, and we try to reconnect
	 */
	public restoreLastStream() : void {
		logging.log(this.TAG, "restoreLastStream", this.streamInfo);

		this.reconnectCount = 0;
		this.tryReconnect(false);
	}

	/**
	 * This is part of the interface, but not actually used
	 */
	reStreamIfChanged(data : any, forceReceiver : string = 'auto') : void {
	}


	/**
	 * Triggered when the playback starts after a play call
	 * Dispatches playing event.
	 * @private
	 */
	private _onPlaying()
	{
		logging.log(this.TAG, "onPlaying");
		this.evt.trigger("playing");

		this.connecting = false;
		this.playing = true;

		this.setVolume(this.volume);
		this.$container.css("opacity", 1);
	}

	/**
	 * Triggered when playing has failed for any reason
	 * Dispatches playFailed event.
	 * @param reason the failure reason
	 * @param duringStartup this is included in the reported data, if it is true, the error occured during startup
	 * 		  (eg. protocol fallback is possible) otherwise the error occured during playing (go to error directly)
	 * @private
	 */
	private _onPlayFailed(reason:string, duringStartup:boolean)
	{
		logging.log(this.TAG, "onPlayFailed: "+reason);

		this.stop();

		this.connecting = false;
		this.playing = false;

		const data = {
			reason: reason,
			duringStartup: duringStartup,
			stream: this.streamInfo.streamName,
			data: {
				receiver: 'webrtc',
				sdpURL: this.sdpURL,
			}
		};

		this.evt.trigger("playFailed", data);
	}

	/**
	 * Starts playing a given stream
	 *
	 * On success / failure _onPlaying and _onPlayFailed are called
	 *
	 * @param sdpUrl sdp url to connect to
	 * @param applicationName application name to use
	 * @param streamName stream name to play
	 */
	public play(sdpUrl : string, applicationName : string, streamName : string) : void
	{
		logging.log(this.TAG, "play", sdpUrl, applicationName, streamName);

		this.playing = false;

		this.stop();  // Kapcsolat lezárása

		this.sdpURL = sdpUrl;
		this.repeaterRetryCount = 0;

		this.streamInfo.applicationName = applicationName;
		this.streamInfo.streamName = streamName;

		this.reconnectCount = 0;
		this.tryReconnect(false);
	}

	/**
	 * Tries to reconnect to current stream
	 * @param canDoFallback if true, error was detected around ice candidates, and falling back to tcp is justified
	 * @param reason the reason to give on failure if no more tries are available
	 */
	private tryReconnect(canDoFallback:boolean = false, reason:string = "No more retries"):void {
		if (!this.initialized) return;

		if (this.reconnectTimer != null) {
			clearTimeout(this.reconnectTimer);
		}
		if (this.videoFrameDetectionTimer != null) {
			clearInterval(this.videoFrameDetectionTimer);
			this.videoFrameDetectionTimer = null;
		}

		if (this.reconnectCount < this.reconnectMaxChance) {

			// if we can do fallback, and fallback is possible, do so
			if (!this.iceCandidateTcpOnly && canDoFallback) {
				const isChrome = !!window['chrome'] && (!!window['chrome']['webstore'] || !!window['chrome']['runtime']);
				if (isChrome) {
					this.iceCandidateTcpOnly = true;
					logging.log(this.TAG, "Activating TCP fallback");
				}
			}

			this.reconnectCount++;
			logging.log(this.TAG, "Connection try #"+this.reconnectCount);
			this.connecting = true;
			this.stop();
			this.reconnectTimer = setTimeout(() => {
				this.reconnectTimer = null; // timer is not active anymore
				if (this.playing) {
					return; // onPlaying has been called already
				}

				// timeout
				logging.log(this.TAG, "Connection timeout");
				this.tryReconnect(true, "timeout");
			}, this.reconnectTimeout);

			this.wsConnect();
		} else {
			this._onPlayFailed(reason, true);
		}
	}

	/**
	 * Stops current stream
	 */
	public stop():void
	{
		logging.log(this.TAG, "Stop");

		if (!this.initialized) return;

		this.playing = false;

		if(this.remoteVideo !== null) {
			this.remoteVideo.pause();
			this.remoteVideo.removeAttribute('src'); // empty source
			this.remoteVideo.load();
		}

		if (this.videoFrameDetectionTimer) {
			clearInterval(this.videoFrameDetectionTimer);
			this.videoFrameDetectionTimer = null;
		}
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.peerConnection != null) {
			this.peerConnection.close();
			this.peerConnection = null;
		}
		if (this.wsConnection != null) {
			this.wsConnection.close();
			this.wsConnection = null;
		}
	}

	// Wowza/WebRtc specific code below

	/**
	 * Initiate websocket connection
	 * Control is transferred to wsOnOpen when connection is successful
	 * @private
	 */
	private wsConnect():void
	{
		logging.log(this.TAG, "wsConnect");

		this.wsConnection = new WebSocket(this.sdpURL);
		this.wsConnection.binaryType = 'arraybuffer';

		this.wsConnection.onopen = () => { this.wsOnOpen(); }
		this.wsConnection.onmessage = (evt) => { this.wsOnMessage(evt); }
		this.wsConnection.onclose = () => {
			logging.log(this.TAG, "wsConnection closed");
		}
		this.wsConnection.onerror = (evt) => {
			logging.error(this.TAG, "wsConnection error", evt);
			this.tryReconnect(false, "wsConnection error");
		}
	}

	/**
	 * Connection to websocket successful, send initial getOffer request
	 * @private
	 */
	private wsOnOpen():void {
		logging.log(this.TAG,"wsConnection.onopen");

		let peerConnectionConfig = {
			'iceServers': []
		};

		this.peerConnection = new RTCPeerConnection(peerConnectionConfig);
		this.peerConnection.onicecandidate = (event) => {
			if (event.candidate) {
				// we ignore local ice candidates as wowza doesn't use these
			}
			else {
				// if the candidate field is null, we are at the end of local candidate list
				// also ignored for now
			}
		}
		this.peerConnection.oniceconnectionstatechange = (event) => {
			this.stateChangeIceCandidate(event);
		}

		this.peerConnection.ontrack = (event) => { this.gotRemoteTrack(event); }

		this.sendPlayGetOffer();
	}

	/**
	 * Send a play / getOffer request to wowza
	 * @private
	 */
	private sendPlayGetOffer():void {
		logging.log(this.TAG, "sendPlayGetOffer", this.streamInfo);

		this.wsConnection.send( JSON.stringify({
				direction: "play",
				command: "getOffer",
				streamInfo: this.streamInfo,
				userData: this.userData
			}
		));
	}

	/**
	 * Handle incoming message from wowza websocket connection
	 * @param evt
	 * @private
	 */
	private wsOnMessage(evt:any):void {
		logging.log(this.TAG, "wsConnection.onmessage", evt.data);

		const msgJSON = JSON.parse(evt.data);

		const msgStatus = Number(msgJSON['status']);
		const msgCommand = msgJSON['command'];

		if (msgStatus == 514) // repeater stream not ready
		{
			this.repeaterRetryCount++;

			if (this.repeaterRetryCount < 10) {
				logging.log(this.TAG, 'Live stream repeater timeout, will retry');

				setTimeout(this.sendPlayGetOffer, 500);
			} else {
				logging.error(this.TAG, 'Live stream repeater timeout, giving up');

				this.tryReconnect(false,"Live stream repeater timeout");
			}
		} else if (msgStatus != 200) {
			logging.error(this.TAG, "error: "+msgJSON['statusDescription']);

			this.tryReconnect(false, msgJSON['statusDescription']);
		} else {
			const streamInfoResponse = msgJSON['streamInfo'];
			if (streamInfoResponse !== undefined) {
				this.streamInfo.sessionId = streamInfoResponse.sessionId;
			}

			// sdp arrives in first message
			const sdpData = msgJSON['sdp'];
			if (sdpData !== undefined) {

				this.peerConnection.setRemoteDescription(new RTCSessionDescription(sdpData))
					.then(() => {
						this.peerConnection.createAnswer()
							.then((description) => { this.gotDescription(description); })
							.catch((error) => {
								logging.error(this.TAG,"createAnswer error", error)
								this.tryReconnect(true, "createAnswer error");
							});
					})
					.catch((err) => {
						logging.error(this.TAG,"setRemoteDescription error", err)
						this.tryReconnect(true, "setRemoteDescription error");
					});
			}


			// ice candidates in the second message
			const iceCandidates = msgJSON['iceCandidates'];
			if (iceCandidates !== undefined) {
				this.addIceCandidates(iceCandidates)
					.then(() => {
						logging.log(this.TAG, "Finished adding ice candidates");
						// after this, connection negotiation and playback will start soon
					})
					.catch((err) => {
						// something went wrong
						logging.error(this.TAG, "Failed to add ice candidates", err);
						this.tryReconnect(true, err);
					});
			}

		}

		if ('sendResponse'.localeCompare(msgCommand) == 0) {
			logging.log(this.TAG, "Finished signaling, closing websocket connection");

			if (this.wsConnection != null) this.wsConnection.close();

			this.wsConnection = null;
		}
	}

	/**
	 * Add ice candidates based on input from JSON
	 * Refactored as async function for better error handling
	 * @param iceCandidates candidates as from json
	 * @private
	 */
	private async addIceCandidates(iceCandidates: any):Promise<void>
	{
		for(let index in iceCandidates) {
			let c = iceCandidates[index];

			logging.log(this.TAG, 'processing ice candidate: '+JSON.stringify(c['candidate']));

			let parts = c['candidate'].split(" ");
			// https://tools.ietf.org/id/draft-ietf-mmusic-ice-sip-sdp-14.html#rfc.section.5
			// 0: foundation
			// 1: component-id
			// 2: transport
			// 3: priority
			// 4: address
			// 5: port
			// 6: typ
			// 7: candidate type ("host")
			// 8+: extensions

			/*
			priority = (2^24)*(type preference) +
					   (2^8)*(local preference) +
					   (2^0)*(256 - component ID)
					   https://tools.ietf.org/html/draft-ietf-ice-rfc5245bis-00#section-4.1.2
					   https://tools.ietf.org/html/rfc8445#section-5.1.2.1
			*/

			if (this.iceCandidateTcpOnly  && parts[2].toLowerCase() == "udp") {
				logging.log(this.TAG, 'Skipping UDP candidate');
				continue;
			}

			let typePreference = 50;
			let localPreference = 50;
			let priorityBase = 256-parseInt(parts[1]);

			parts[3] = String((typePreference << 24) + (localPreference << 8) + priorityBase);

			c['candidate'] = parts.join(" ");

			// c['sdpMid'] = 0;
			c['sdpMLineIndex'] = 0;

			logging.log(this.TAG, 'standardized remote ice candidate: '+JSON.stringify(c));
			await this.peerConnection.addIceCandidate(new RTCIceCandidate(c));
			logging.log(this.TAG, "iceCandidate added");

			// c['sdpMid'] = 1;
			// this only works if there is sound - so if an exception is caught, might not have sound in sdp
			c['sdpMLineIndex'] = 1;
			logging.log(this.TAG, 'standardized remote ice candidate: ' + JSON.stringify(c));
			try {
				await this.peerConnection.addIceCandidate(new RTCIceCandidate(c));
				logging.log(this.TAG, "iceCandidate added");
			}
			catch (err) {
				logging.error(this.TAG, "failed to add iceCandidate for mLineIndex=1, maybe no sound in stream?", err);
			}
		}
	}

	/**
	 * Received local stream description, send it through websocket
	 * @param description
	 * @private
	 */
	private gotDescription(description):void  {
		logging.log(this.TAG, "gotDescription", description);

		this.peerConnection.setLocalDescription(description)
			.then(() => {
				this.wsConnection.send(JSON.stringify(
					{
						direction: "play",
						command: "sendResponse",
						streamInfo: this.streamInfo,
						sdp: description,
						userData: this.userData
					}
				));
			})
			.catch((error) => {
				logging.error(this.TAG, "setLocalDescription error", error);
				this.tryReconnect(true, "setLocalDescription error");
			});
	}

	/**
	 * Received a remote track, ready to play
	 * @param event
	 * @private
	 */
	private gotRemoteTrack(event):void {
		logging.log(this.TAG, "gotRemoteTrack", event.streams[0]);

		try {
			this.remoteVideo.srcObject = event.streams[0];
		} catch (error){
			this.remoteVideo.src = window.URL.createObjectURL(event.streams[0]);
		}
	}

	/**
	 * Handle ice connection state changes
	 * @param event the state change event
	 * @private
	 */
	private stateChangeIceCandidate(event:Event):void {
		if (event.currentTarget != this.peerConnection) {
			logging.log(this.TAG, "iceConnectionState change received, with invalid target - ignoring", event.currentTarget);
			return;
		}
		logging.log(this.TAG, "iceConnectionState change", this.peerConnection.iceConnectionState);
		if(this.peerConnection.iceConnectionState == 'failed' ||
			this.peerConnection.iceConnectionState == 'disconnected' ||
			this.peerConnection.iceConnectionState == 'closed')
		{
			if (this.playing) {
				this._onPlayFailed("iceConnectionState:"+this.peerConnection.iceConnectionState, false);
			}
			else if (this.connecting) {
				this.tryReconnect(true, "iceConnectionState:"+this.peerConnection.iceConnectionState);
			}
		}
		else if (this.peerConnection.iceConnectionState === "connected") {
			logging.log(this.TAG, "connected");

			// when we get the connected event, start looking for indication of successful connection
			// the cross-platform working solution is to look for the inbound-rtp video statistics
			// and check if any frames has been decoded or any bytes has been received
			// this later allows for faster detection and also some safety as secondary check
			this.videoFrameDetectionCounter = this.videoFrameDetectionTimeout;

			this.videoFrameDetectionTimer = setInterval(()=> {
					this.peerConnection.getStats(null).then(stats => {
						let videoFrameDetected:boolean = false;
						//
						stats.forEach(report => {
							if (report.type && report.type == 'inbound-rtp' && report.kind && report.kind == 'video') {
								//
								if ((report.framesDecoded && report.framesDecoded > 0) ||
									(report.bytesReceived && report.bytesReceived > 0)) {
									videoFrameDetected = true;
								}
							}
						});
						if (videoFrameDetected) {
							clearInterval(this.videoFrameDetectionTimer);
							this.videoFrameDetectionTimer = null;
							this._onPlaying();
						}
						else {
							this.videoFrameDetectionCounter--;
							if (this.videoFrameDetectionCounter == 0) {
								clearInterval(this.videoFrameDetectionTimer);
								logging.error(this.TAG, "Couldn't detect incoming video frames in time");
								this.tryReconnect(true,"couldn't detect incoming video frames");
							}
						}
					});
				}
			, this.videoFrameDetectionFrequency);
		}
	}
}
